import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Library,
  Archive,
  ArrowLeft,
  Plus,
  Search,
  X,
  LockKeyhole,
  Ellipsis,
} from "lucide-react";
import type { WorkspaceModel } from "./useWorkspace";
import { api, notify } from "./api";
import {
  libraryPreview,
  type LibraryItem,
  type LibraryBundle,
  type LibraryPreview,
} from "../shared/library";
import { taskQueue } from "../shared/images";
import { captureLibrarySelection, insertLibraryItem } from "./library-content";
import { LibraryPlacementPreview } from "./LibraryPlacementTool";
import { sheetAt } from "./sheet-operations";
import { Modal } from "./Modal";
import "./library.css";

const previewQueue = taskQueue(2);
const dragType = "application/x-calque-library";
export function LibraryPanel({
  model,
  close,
  open = true,
  saveRequest = 0,
  initialItem,
  onSaveHandled,
  onItemHandled,
  chooseProject,
}: {
  model?: WorkspaceModel;
  close: () => void;
  open?: boolean;
  saveRequest?: number;
  initialItem?: string;
  onSaveHandled?: () => void;
  onItemHandled?: () => void;
  chooseProject?: (id?: string) => void;
}) {
  const [items, setItems] = useState<LibraryItem[]>([]);
  const [previews, setPreviews] = useState<Record<string, LibraryPreview>>({});
  const [query, setQuery] = useState("");
  const [filter, setFilter] = useState("All types");
  const [sort, setSort] = useState("recent");
  const [archived, setArchived] = useState(false);
  const [selected, setSelected] = useState<string | null>(initialItem ?? null);
  const [draft, setDraft] = useState<LibraryBundle | null>(null);
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [loadError, setLoadError] = useState("");
  const [editing, setEditing] = useState<"rename" | "delete" | null>(null);
  const [placing, setPlacing] = useState<string | null>(null);
  const [drag, setDrag] = useState<{
    id: string;
    x: number;
    y: number;
    valid: boolean;
  } | null>(null);
  const [savedItem, setSavedItem] = useState<LibraryItem | null>(null);
  const refreshVersion = useRef(0);
  const refreshRunning = useRef(false);
  const refreshPending = useRef(false);
  const placementEpoch = useRef(0);
  const visible = useRef(open);
  visible.current = open;
  const placementTrigger = useRef<HTMLElement | null>(null);
  const [undoArchive, setUndoArchive] = useState<LibraryItem | null>(null);
  const active = useRef(true),
    working = useRef(false),
    controller = useRef(new AbortController());
  const insertion = useRef<AbortController | null>(null);
  const dragged = useRef<string | null>(null),
    search = useRef<HTMLInputElement>(null);
  const saveName = useRef<HTMLInputElement>(null),
    placeButton = useRef<HTMLButtonElement>(null);
  const bundleCache = useRef(new Map<string, LibraryBundle>());
  const item = items.find((i) => i.id === selected);
  const preview = item && previews[item.id];
  const canPlace = !!model?.canEdit;
  const refresh = async () => {
    if (working.current) {
      refreshPending.current = true;
      return;
    }
    refreshPending.current = false;
    refreshRunning.current = true;
    const version = ++refreshVersion.current;
    try {
      const data = await api<{ items: LibraryItem[] }>("/api/library");
      if (active.current && version === refreshVersion.current) {
        setItems(data.items);
        setLoadError("");
      }
    } catch (e) {
      if (active.current && version === refreshVersion.current)
        setLoadError((e as Error).message);
    } finally {
      if (active.current && version === refreshVersion.current) {
        refreshRunning.current = false;
        setLoading(false);
      }
    }
  };
  const run = async (label: string, action: () => Promise<void>) => {
    if (working.current) return;
    // A list captured before a save must never replace the newly saved item.
    if (refreshRunning.current) {
      refreshVersion.current++;
      refreshRunning.current = false;
      refreshPending.current = true;
    }
    working.current = true;
    setBusy(label);
    setError("");
    try {
      await action();
    } catch (e) {
      if (active.current && (e as Error).name !== "AbortError")
        setError((e as Error).message);
    } finally {
      working.current = false;
      if (active.current) setBusy("");
      if (active.current && visible.current && refreshPending.current)
        void refresh();
    }
  };
  useEffect(() => {
    onItemHandled?.();
    active.current = true;
    controller.current = new AbortController();
    return () => {
      active.current = false;
      refreshVersion.current++;
      controller.current.abort();
      model?.editor.setHintingShapes([]);
      if (model?.editor.isIn("library-place"))
        model.editor.setCurrentTool("select");
    };
  }, []);
  useEffect(() => {
    if (!open) {
      refreshVersion.current++;
      refreshRunning.current = false;
      placementEpoch.current++;
      insertion.current?.abort();
      if (model?.editor.isIn("library-place"))
        model.editor.setCurrentTool("select");
      model?.editor.setHintingShapes([]);
      dragged.current = null;
      setDrag(null);
      setPlacing(null);
      setEditing(null);
      return;
    }
    void refresh();
    requestAnimationFrame(() =>
      draft ? saveName.current?.focus() : search.current?.focus(),
    );
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const resume = () => {
      if (document.visibilityState === "visible") void refresh();
    };
    window.addEventListener("focus", resume);
    window.addEventListener("online", resume);
    document.addEventListener("visibilitychange", resume);
    return () => {
      window.removeEventListener("focus", resume);
      window.removeEventListener("online", resume);
      document.removeEventListener("visibilitychange", resume);
    };
  }, [open]);
  useEffect(() => {
    if (!savedItem) return;
    const timer = setTimeout(() => setSavedItem(null), 8000);
    return () => clearTimeout(timer);
  }, [savedItem]);
  useEffect(() => {
    let live = true;
    for (const value of items) {
      if (previews[value.id]) continue;
      void previewQueue(async () => {
        if (!live) return;
        try {
          const data = await api<LibraryPreview>(
            `/api/library/${value.id}/preview`,
          );
          if (live)
            setPreviews((current) => ({ ...current, [value.id]: data }));
        } catch {
          /* Item remains accessible with a labelled preview fallback. */
        }
      });
    }
    return () => {
      live = false;
    };
  }, [items]);
  useEffect(() => {
    if (draft) saveName.current?.focus();
  }, [draft]);
  const prepareSave = () => {
    if (!model) {
      chooseProject?.();
      return;
    }
    if (!model.canEdit || !model.selected.length) {
      setError(
        model.canEdit
          ? "Select objects on the board, then choose Save to My Library."
          : "You need edit access to save from this project.",
      );
      return;
    }
    void run("Preparing your selection…", async () => {
      const bundle = await captureLibrarySelection(model.editor);
      if (!active.current) return;
      setDraft(bundle);
      setName("");
      setSelected(null);
    });
  };
  const lastSave = useRef(0);
  useEffect(() => {
    if (saveRequest && lastSave.current !== saveRequest) {
      lastSave.current = saveRequest;
      prepareSave();
      onSaveHandled?.();
    }
    if (!saveRequest) lastSave.current = 0;
  }, [saveRequest]);
  useEffect(() => {
    const cancel = (event: KeyboardEvent) => {
      if (event.key === "Escape") placementEpoch.current++;
      if (
        event.key === "Escape" &&
        insertion.current &&
        !insertion.current.signal.aborted
      )
        insertion.current.abort();
    };
    window.addEventListener("keydown", cancel);
    return () => window.removeEventListener("keydown", cancel);
  }, []);
  const cancelPlacement = () => {
    placementEpoch.current++;
    insertion.current?.abort();
    if (model?.editor.isIn("library-place"))
      model.editor.setCurrentTool("select");
    setPlacing(null);
    requestAnimationFrame(() =>
      (placementTrigger.current?.isConnected
        ? placementTrigger.current
        : placeButton.current
      )?.focus(),
    );
  };
  const commit = async (id: string, point: { x: number; y: number }) => {
    if (!model?.canEdit || working.current) return;
    const sheet = sheetAt(model.editor, point);
    if (
      sheet &&
      (model.editor.isShapeHidden(sheet) ||
        model.editor.isShapeOrAncestorLocked(sheet))
    ) {
      setError("Choose the canvas or an unlocked sheet to place a copy.");
      return;
    }
    const operation = new AbortController();
    insertion.current = operation;
    const signal = AbortSignal.any([
      controller.current.signal,
      operation.signal,
    ]);
    await run("Placing copy…", async () => {
      const bundle =
        bundleCache.current.get(id) ??
        (await api<LibraryBundle>(`/api/library/${id}`));
      signal.throwIfAborted();
      await insertLibraryItem(model.editor, model.room, bundle, point, signal);
      if (active.current) {
        setPlacing(null);
        notify("Copy placed. Undo removes it.");
      }
    });
    if (insertion.current === operation) insertion.current = null;
  };
  const beginPlace = (id: string) => {
    if (working.current) return;
    if (!model) {
      chooseProject?.(id);
      return;
    }
    if (!canPlace) {
      setError("You need edit access to place a copy in this project.");
      return;
    }
    placementTrigger.current =
      document.activeElement instanceof HTMLElement
        ? document.activeElement
        : null;
    const epoch = ++placementEpoch.current;
    if (model.editor.isIn("library-place"))
      model.editor.setCurrentTool("select");
    void run("Preparing copy…", async () => {
      const bundle = await api<LibraryBundle>(`/api/library/${id}`);
      if (!active.current || !visible.current || epoch !== placementEpoch.current)
        return;
      bundleCache.current.clear();
      bundleCache.current.set(id, bundle);
      setPlacing(id);
      setPreviews((current) => ({ ...current, [id]: libraryPreview(bundle) }));
      model.editor.setCurrentTool("library-place", {
        keyboardPoint: model.editor.getViewportPageBounds().center,
        commit: (point: { x: number; y: number }) => void commit(id, point),
        cancel: () =>
          requestAnimationFrame(() =>
            (placementTrigger.current?.isConnected
              ? placementTrigger.current
              : placeButton.current
            )?.focus(),
          ),
        exit: () => {
          insertion.current?.abort();
          if (active.current) setPlacing(null);
        },
      });
      model.editor.focus();
      // The editor can be logically focused while a library button owns DOM focus.
      model.editor.getContainer().focus();
    });
  };
  useEffect(() => {
    if (!model || !open) return;
    const target = (event: DragEvent) =>
      event.target instanceof Element &&
      !!event.target.closest(".canvas-view") &&
      !event.target.closest("button,input,[role=dialog]");
    const end = () => {
      dragged.current = null;
      setDrag(null);
      model.editor.setHintingShapes([]);
    };
    const over = (event: DragEvent) => {
      if (!dragged.current || !event.dataTransfer?.types.includes(dragType))
        return;
      event.preventDefault();
      event.stopImmediatePropagation();
      const point = model.editor.screenToPage({
        x: event.clientX,
        y: event.clientY,
      });
      const onCanvas = target(event);
      const sheet = onCanvas ? sheetAt(model.editor, point) : undefined;
      const valid =
        onCanvas &&
        (!sheet ||
          (!model.editor.isShapeHidden(sheet) &&
            !model.editor.isShapeOrAncestorLocked(sheet)));
      event.dataTransfer.dropEffect = valid ? "copy" : "none";
      model.editor.setHintingShapes(valid && sheet ? [sheet.id] : []);
      setDrag({
        id: dragged.current,
        x: event.clientX,
        y: event.clientY,
        valid,
      });
    };
    const drop = (event: DragEvent) => {
      const id = dragged.current;
      if (!id) return;
      event.preventDefault();
      event.stopImmediatePropagation();
      end();
      if (target(event) && items.some((i) => i.id === id && !i.archived))
        void commit(
          id,
          model.editor.screenToPage({ x: event.clientX, y: event.clientY }),
        );
    };
    window.addEventListener("dragover", over, true);
    window.addEventListener("drop", drop, true);
    window.addEventListener("dragend", end);
    return () => {
      window.removeEventListener("dragover", over, true);
      window.removeEventListener("drop", drop, true);
      window.removeEventListener("dragend", end);
    };
  }, [model, items, open]);
  const update = (
    value: LibraryItem,
    patch: { name?: string; archived?: boolean },
  ) =>
    void run("Saving changes…", async () => {
      await api(`/api/library/${value.id}`, patch);
      if (!active.current) return;
      setItems((current) =>
        current.map((i) =>
          i.id === value.id
            ? {
                ...i,
                ...patch,
                archived:
                  patch.archived === undefined
                    ? i.archived
                    : Number(patch.archived),
              }
            : i,
        ),
      );
      setEditing(null);
      if (patch.archived !== undefined) {
        setSelected(null);
        setUndoArchive(patch.archived ? value : null);
      }
      notify(
        patch.archived === undefined
          ? "Name saved."
          : patch.archived
            ? "Item archived. Project copies are unchanged."
            : "Item restored.",
      );
    });
  const back = () => {
    cancelPlacement();
    setDraft(null);
    setSelected(null);
    setError("");
    requestAnimationFrame(() => search.current?.focus());
  };
  const thumbnail = (p?: LibraryPreview, large = false) => (
    <div className={`library-thumbnail${large ? " large" : ""}`}>
      {p?.image ? (
        <img src={p.image} alt="" />
      ) : (
        <span>
          <Library size={large ? 42 : 28} />
          <small>Preview unavailable</small>
        </span>
      )}
    </div>
  );
  const filtered = items
    .filter(
      (i) =>
        Boolean(i.archived) === archived &&
        i.name.toLocaleLowerCase().includes(query.trim().toLocaleLowerCase()) &&
        (filter === "All types" || previews[i.id]?.kinds.includes(filter)),
    )
    .sort((a, b) =>
      sort === "name" ? a.name.localeCompare(b.name) : b.created - a.created,
    );
  const gallery = !!model || (!draft && !selected);
  const contents = (
    <section
      className={`library-panel${model ? " library-board" : " library-full"}${placing ? " is-placing" : ""}`}
      hidden={!open}
      aria-label="My Library"
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          placementEpoch.current++;
          e.preventDefault();
          e.stopPropagation();
          if (busy || editing) return;
          if (placing) cancelPlacement();
          else if (draft || selected) back();
          else close();
        }
      }}
    >
      <header className="library-heading">
        <div>
          <h2>My Library</h2>
          <p>
            <LockKeyhole size={12} /> Your pieces, across projects
          </p>
        </div>
        <button aria-label="Close My Library" onClick={close}>
          <X size={18} />
        </button>
      </header>
      {busy && (
        <p className="library-status" role="status">
          {busy}
          {(busy === "Placing copy…" || busy === "Preparing copy…") && (
            <button onClick={cancelPlacement}>Cancel</button>
          )}
        </p>
      )}
      {(error || loadError) && (
        <div className="library-error" role="alert">
          {error || loadError}
          <button
            disabled={!!busy}
            onClick={() => void refresh()}
          >
            Reload library
          </button>
        </div>
      )}
      {draft && (
        <form
          className="library-quick-save"
          onSubmit={(e) => {
            e.preventDefault();
            if (!name.trim()) return;
            void run("Saving item…", async () => {
              const saved = await api<LibraryItem>("/api/library", {
                name: name.trim(),
                bundle: draft,
              });
              if (!active.current) return;
              setItems((current) => [saved, ...current]);
              setPreviews((current) => ({
                ...current,
                [saved.id]: libraryPreview(draft),
              }));
              setDraft(null);
              setSelected(null);
              setSavedItem(saved);
              notify(`Saved “${saved.name}” to My Library.`);
              model?.editor.focus();
            });
          }}
        >
          <div className="library-quick-save-row">
            {thumbnail(libraryPreview(draft))}
            <label>
              Save selection
              <input
                ref={saveName}
                aria-label="Library item name"
                placeholder="Give this piece a name…"
                maxLength={100}
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>
          </div>
          <div className="library-quick-save-actions">
            <small>Private copy. Your original stays on the board.</small>
            <button
              type="button"
              disabled={!!busy}
              onClick={() => {
                setDraft(null);
                model?.editor.focus();
              }}
            >
              Cancel
            </button>
            <button className="primary" disabled={!!busy || !name.trim()}>
              Save item
            </button>
          </div>
        </form>
      )}
      <div className="library-browse" hidden={!gallery}>
        <div className="library-tabs">
          <button
            aria-pressed={!archived}
            onClick={() => {
              setArchived(false);
              setUndoArchive(null);
            }}
          >
            Saved items <small>{items.filter((i) => !i.archived).length}</small>
          </button>
          <button
            aria-pressed={archived}
            onClick={() => {
              setArchived(true);
              setUndoArchive(null);
            }}
          >
            Archived <small>{items.filter((i) => i.archived).length}</small>
          </button>
        </div>
        <div className="library-controls">
          <label className="library-search">
            <Search size={15} />
            <input
              ref={search}
              aria-label="Search library"
              placeholder="Find a saved piece…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </label>
          <div>
            <select
              aria-label="Filter library"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
            >
              {["All types", "Drawings", "Images", "Text & tables"].map(
                (value) => (
                  <option key={value}>{value}</option>
                ),
              )}
            </select>
            <select
              aria-label="Sort library"
              value={sort}
              onChange={(e) => setSort(e.target.value)}
            >
              <option value="recent">Recently saved</option>
              <option value="name">Name A–Z</option>
            </select>
          </div>
        </div>
        <div className="library-result-label">
          <span>{loading ? "Loading…" : `${filtered.length} ${filtered.length === 1 ? "item" : "items"}`}</span>
          <span>
            {model && canPlace && !archived
              ? "Drag or click an item to add it"
              : "Choose an item to preview"}
          </span>
        </div>
        <div className="library-gallery-scroll">
          <div className="library-gallery">
            {loading
              ? Array.from({ length: 4 }, (_, i) => (
                  <div
                    key={i}
                    className="library-skeleton"
                    aria-label="Loading library"
                  />
                ))
              : filtered.map((value) => (
                  <article
                    key={value.id}
                    className={
                      selected === value.id
                        ? "is-previewed"
                        : savedItem?.id === value.id
                          ? "just-saved"
                          : undefined
                    }
                  >
                    <button
                      className="library-card"
                      aria-label={`${model && canPlace && !value.archived ? "Add" : "Preview"} ${value.name}`}
                      draggable={canPlace && !busy && !archived}
                      onDragStart={(e) => {
                        if (!canPlace || busy || value.archived) {
                          e.preventDefault();
                          return;
                        }
                        dragged.current = value.id;
                        e.dataTransfer.effectAllowed = "copy";
                        e.dataTransfer.setData(dragType, value.id);
                      }}
                      onClick={() => {
                        if (model && canPlace && !value.archived) {
                          beginPlace(value.id);
                        } else {
                          setSelected(value.id);
                          setError("");
                        }
                      }}
                    >
                      {thumbnail(previews[value.id])}
                      <strong title={value.name}>{value.name}</strong>
                    </button>
                    <div className="library-card-meta">
                      <small>
                        {previews[value.id]
                          ? `${previews[value.id].count} ${previews[value.id].count === 1 ? "object" : "objects"}`
                          : "Saved selection"}
                      </small>
                      <button
                        className="library-item-menu"
                        aria-label={`Details for ${value.name}`}
                        aria-expanded={selected === value.id}
                        onClick={() => {
                          setSelected((current) =>
                            current === value.id ? null : value.id,
                          );
                          setError("");
                        }}
                      >
                        <Ellipsis size={16} />
                      </button>
                    </div>
                  </article>
                ))}
          </div>
          {!loading && !filtered.length && !draft && (
            <div className="library-empty">
              <Library size={36} />
              <h3>
                {query || filter !== "All types"
                  ? "No matching items"
                  : archived
                    ? "No archived items"
                    : "Keep a piece for next time."}
              </h3>
              <p>
                {query || filter !== "All types"
                  ? "Try another name or clear the filters."
                  : archived
                    ? "Archived items stay here until you restore or delete them."
                    : "Select objects in a project and save them here. Reuse a furniture arrangement, drawing detail or material image."}
              </p>
              {query || filter !== "All types" ? (
                <button
                  onClick={() => {
                    setQuery("");
                    setFilter("All types");
                  }}
                >
                  Clear search & filters
                </button>
              ) : (
                !archived && (
                  <button
                    disabled={!!busy || (!!model && !canPlace)}
                    onClick={prepareSave}
                  >
                    {model ? "Save a selection" : "Open a project to save"}
                  </button>
                )
              )}
            </div>
          )}
        </div>
        {savedItem && (
          <div className="library-feedback" role="status">
            <span>Saved “{savedItem.name}”</span>
            <button
              onClick={() => {
                setSelected(savedItem.id);
                setSavedItem(null);
              }}
            >
              View
            </button>
            <button
              aria-label="Dismiss saved item notice"
              onClick={() => setSavedItem(null)}
            >
              <X size={13} />
            </button>
          </div>
        )}
        {undoArchive && (
          <div className="library-feedback">
            Item archived.
            <button
              disabled={!!busy}
              onClick={() => update(undoArchive, { archived: false })}
            >
              Undo
            </button>
          </div>
        )}
        <footer className="library-footer">
          <button
            className="primary"
            disabled={
              !!busy ||
              !!draft ||
              (!!model && (!canPlace || !model.selected.length))
            }
            title={
              model && !model.selected.length
                ? "Select objects on the board to save them"
                : undefined
            }
            onClick={prepareSave}
          >
            <Plus size={15} />
            {model ? "Save selection" : "Save from a project"}
          </button>
          <details className="library-storage">
            <summary>
              {archived || items.length >= 40
                ? `${items.length}/50 items`
                : "Storage"}
            </summary>
            <p>
              {items.length}/50 items ·{" "}
              {(items.reduce((sum, i) => sum + i.bytes, 0) / 1e6).toFixed(1)}/50
              MB. Archived items still use storage.
            </p>
          </details>
        </footer>
      </div>
      {!draft && selected && !item && !loading && (
        <div className="library-empty">
          <h3>Item unavailable</h3>
          <p>It may have been removed in another session.</p>
          <button onClick={back}>Back to saved items</button>
        </div>
      )}
      {model && item && !draft && (
        <div className="library-peek" aria-label={`Preview ${item.name}`}>
          <div className="library-peek-top">
            {thumbnail(preview)}
            <div>
              <strong>{item.name}</strong>
              <small>
                {preview
                  ? `${preview.count} editable ${preview.count === 1 ? "object" : "objects"}`
                  : "Saved selection"}
              </small>
            </div>
            <button
              aria-label="Close item preview"
              onClick={() => setSelected(null)}
            >
              <X size={15} />
            </button>
          </div>
          <div className="library-peek-actions">
            <button
              disabled={!!busy || !!placing}
              onClick={() => {
                setName(item.name);
                setEditing("rename");
              }}
            >
              Rename
            </button>
            <button
              disabled={!!busy || !!placing}
              onClick={() =>
                item.archived
                  ? setEditing("delete")
                  : update(item, { archived: true })
              }
            >
              {item.archived ? "Delete…" : "Archive"}
            </button>
            {item.archived && (
              <button
                disabled={!!busy}
                onClick={() => update(item, { archived: false })}
              >
                Restore item
              </button>
            )}
          </div>
          <p className="library-peek-note">
            Independent, unscaled copy. Your edits won't change the saved item.
          </p>
        </div>
      )}
      {!model && !draft && item && (
        <div className="library-detail">
          <div className="library-detail-nav">
            <button className="library-back" disabled={!!busy} onClick={back}>
              <ArrowLeft size={14} />
              All items
            </button>
            <button
              disabled={!!busy || !!placing}
              onClick={() => {
                setName(item.name);
                setEditing("rename");
              }}
            >
              Rename
            </button>
            <button
              disabled={!!busy || !!placing}
              onClick={() =>
                item.archived
                  ? setEditing("delete")
                  : update(item, { archived: true })
              }
            >
              {item.archived ? (
                "Delete…"
              ) : (
                <>
                  <Archive size={13} />
                  Archive
                </>
              )}
            </button>
          </div>
          <div className="library-detail-scroll">
            {thumbnail(preview, true)}
            <span className="library-eyebrow">
              {item.archived
                ? "ARCHIVED ITEM"
                : preview?.kinds.join(" + ") || "SAVED SELECTION"}
            </span>
            <h3>{item.name}</h3>
            <p>
              {preview
                ? `${preview.count} editable ${preview.count === 1 ? "object" : "objects"}`
                : "Editable saved selection"}
            </p>
            <dl>
              <div>
                <dt>Drawing scale</dt>
                <dd>Unscaled copy</dd>
              </div>
              <div>
                <dt>Saved</dt>
                <dd>
                  {new Date(item.created).toLocaleDateString(undefined, {
                    day: "numeric",
                    month: "short",
                    year: "numeric",
                  })}
                </dd>
              </div>
            </dl>
            <p className="library-copy-note">
              Place an editable copy. Changes in your project won’t change this
              saved item.
            </p>
            <p>
              Copies keep their saved canvas size. Review dimensions after
              placing on a different drawing.
            </p>
          </div>
          <div className="library-detail-actions">
            {placing ? (
              <>
                <span>Click the canvas to place · Esc cancels</span>
                <button disabled={!!busy} onClick={cancelPlacement}>
                  Cancel placement
                </button>
              </>
            ) : (
              <button
                ref={placeButton}
                className="primary"
                disabled={!!busy || (!!model && !canPlace && !item.archived)}
                onClick={() =>
                  item.archived
                    ? update(item, { archived: false })
                    : beginPlace(item.id)
                }
              >
                {item.archived
                  ? "Restore item"
                  : "Choose a project…"}
              </button>
            )}
          </div>
        </div>
      )}
      {open &&
        model &&
        placing &&
        createPortal(
          <>
            <LibraryPlacementPreview
              editor={model.editor}
              preview={previews[placing]}
            />
            <div className="library-placement-bar" role="status">
              <strong>Place {items.find((i) => i.id === placing)?.name}</strong>
              <span>
                Unscaled copy · Click anywhere · Enter centers in view · Esc cancels
              </span>
              <button onClick={cancelPlacement}>
                Cancel
              </button>
            </div>
          </>,
          document.body,
        )}
      {open &&
        drag &&
        createPortal(
          <div
            className={`library-drag-preview${drag.valid ? " valid" : ""}`}
            style={{ left: drag.x + 18, top: drag.y + 18 }}
          >
            {previews[drag.id]?.image && (
              <img src={previews[drag.id].image} alt="" />
            )}
            <span>
              {drag.valid
                ? "Drop an unscaled copy"
                : "Move onto the canvas"}
            </span>
          </div>,
          document.body,
        )}
      {editing && item && (
        <Modal
          title={
            editing === "rename" ? "Rename saved item" : "Delete saved item?"
          }
          close={() => {
            if (!busy) setEditing(null);
          }}
        >
          {editing === "rename" ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                update(item, { name });
              }}
            >
              <label>
                Name
                <input
                  aria-label="Rename saved item"
                  maxLength={100}
                  required
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                />
              </label>
              <button className="primary" disabled={!!busy || !name.trim()}>
                Save name
              </button>
            </form>
          ) : (
            <>
              <p>
                “{item.name}” will be permanently removed from your private
                library. Copies in projects stay as they are.
              </p>
              <div className="library-detail-actions">
                <button disabled={!!busy} onClick={() => setEditing(null)}>
                  Cancel
                </button>
                <button
                  className="danger"
                  disabled={!!busy}
                  onClick={() =>
                    void run("Deleting saved item…", async () => {
                      await api(`/api/library/${item.id}/delete`, {
                        confirm: true,
                      });
                      if (!active.current) return;
                      setItems((current) =>
                        current.filter((i) => i.id !== item.id),
                      );
                      bundleCache.current.delete(item.id);
                      setEditing(null);
                      setSelected(null);
                      notify(
                        "Saved item deleted. Project copies are unchanged.",
                      );
                    })
                  }
                >
                  Delete permanently
                </button>
              </div>
            </>
          )}
          {error && <p role="alert">{error}</p>}
        </Modal>
      )}
    </section>
  );
  return model ? createPortal(contents, document.body) : contents;
}
